import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Hash } from "lucide-react";
import { useState } from "react";
import { AdminTable, type AdminTableColumn } from "@/components/ui/admin-table";
import { PageHeader } from "@/components/ui/page-header";
import { contractsApi, type ProducerRow } from "@/features/contracts/contracts-api";
import { ProductSearchBar } from "@/features/products/components/product-search-bar";
import { ensureElementVisible } from "@/lib/route-guards";
import { useDebounce } from "@/lib/use-debounce";

export const Route = createFileRoute("/_auth/kartoteka/producenci")({
	beforeLoad: async ({ context, location }) => {
		await ensureElementVisible(
			context.queryClient,
			"sidebar.kartoteka.producenci",
			location.pathname,
		);
	},
	component: ProducenciListPage,
});

const columns: AdminTableColumn<ProducerRow>[] = [
	{
		key: "id",
		header: "ID",
		width: "80px",
		// Telefon: identyfikator jako podtytul pod nazwa, nie jako tytul wiersza.
		mobileRole: "subtitle",
		render: (row) => (
			<div className="flex items-center gap-1.5 text-muted-foreground font-mono text-xs">
				<Hash className="size-3 opacity-50" />
				{row.id}
			</div>
		),
	},
	{
		key: "nazwa",
		header: "Nazwa Producenta",
		mobileRole: "title",
		render: (row) => <span className="font-semibold text-sm">{row.nazwa || "—"}</span>,
	},
];

function ProducenciListPage() {
	const [searchQuery, setSearchQuery] = useState("");
	const debouncedQuery = useDebounce(searchQuery, 300);

	const { data: producers = [] } = useQuery({
		queryKey: ["producers", "list-full"],
		queryFn: contractsApi.getProducersList,
	});

	const filteredProducers = producers.filter((p) =>
		p.nazwa?.toLowerCase().includes(debouncedQuery.toLowerCase()),
	);

	return (
		<div className="flex flex-col h-full overflow-hidden">
			<div className="p-6 pb-2 max-md:px-3 max-md:pt-3 max-md:pb-1 max-md:empty:hidden">
				<PageHeader
					title="Producenci"
					description="Lista producentów zsynchronizowana z Twoją bazą danych"
				/>
			</div>

			<div className="px-6 py-3 border-y bg-card/50 flex justify-between items-center max-md:px-3 max-md:py-2">
				<div className="w-full max-w-md">
					<ProductSearchBar
						value={searchQuery}
						onChange={setSearchQuery}
						placeholder="Szukaj po nazwie producenta..."
					/>
				</div>
				<div className="text-xs text-muted-foreground hidden sm:block">
					Liczba producentów: <span className="font-bold">{filteredProducers.length}</span>
				</div>
			</div>

			<div className="flex-1 min-h-0 p-6 pt-3 max-md:p-0">
				<AdminTable
					tableId="kartoteka.producenci"
					columns={columns}
					data={filteredProducers}
					keyField="id"
					emptyMessage="Nie znaleziono producentów."
					className="h-full"
				/>
			</div>
		</div>
	);
}
